import { Link, useNavigate } from 'react-router-dom'

import PositionDoughnutChart, {
  getPositionSlicePercentages,
} from '@Features/position/PositionDoughnutChart'
import ProductPositionIcon from '@Icons/product-position-icon.svg?react'
import type { PositionStatus } from '@Store/types/dashboard'
import SpinLoader from '@Utils/loaders/spin'
import Mixpanel from '@Utils/mixpanel/Mixpanel'

const IS_CHEAP_BY_KEY: Record<keyof PositionStatus, number> = {
  highest: 5,
  higher: 4,
  cheaper: 2,
  cheapest: 1,
  average: 3,
  allequal: 6,
}

// Mixpanel event name per slice. The consolidated card's slice labels differ
// from the ones each page used to send, and existing Mixpanel/Intercom
// reports key on those original names, so each consumer keeps its own.
export type PricePositionEventNames = Record<keyof PositionStatus, string>

// Dashboard's original full-width card.
export const DASHBOARD_PRICE_POSITION_EVENTS: PricePositionEventNames = {
  highest: 'Highest price Clicked',
  higher: 'Higher than average Clicked',
  cheaper: 'Cheaper than average Clicked',
  cheapest: 'Cheapest Clicked',
  average: 'Average Clicked',
  allequal: 'All equal Clicked',
}

// The original compact card: Products sidebar and brand/category report lists.
export const SUMMARY_PRICE_POSITION_EVENTS: PricePositionEventNames = {
  highest: 'Highest price Clicked',
  higher: 'Higher than av. Clicked',
  cheaper: 'Cheaper than av. Clicked',
  cheapest: 'Cheapest price Clicked',
  average: 'Average Clicked',
  allequal: 'All equal Clicked',
}

export default function PricePositionCard({
  position,
  view,
  // Tailwind breakpoints are viewport-width based, not container-width
  // based — this card is used both in a full-width dashboard row and in a
  // ~1/3-width sidebar column, so the same `lg:`/`sm:` classes can't serve
  // both. `compact` picks the narrow-container layout explicitly instead.
  compact = false,
  // Default behavior navigates to the full /products list filtered by
  // is_cheap. Report list pages (brand/category) instead filter their own
  // in-page list, so they override this to update their own query state.
  onSliceClick,
  eventNames,
}: {
  position: PositionStatus | undefined
  view?: string
  compact?: boolean
  onSliceClick?: (isCheap: number, key: keyof PositionStatus) => void
  eventNames: PricePositionEventNames
}): JSX.Element {
  const navigate = useNavigate()

  const sendEvent = (event: string): void => {
    Mixpanel.track(event, {
      page: view,
      section: 'Price Position Card',
    })
  }

  return (
    <div className="flex flex-col sm:col-span-3 border border-solid border-gray-200 rounded-lg shadow mb-16">
      <div className="flex flex-col border-b border-solid border-gray-200 container mx-auto p-6">
        <div className="flex flex-row items-center gap-2 pb-1">
          <ProductPositionIcon />
          <span className="font-medium text-lg text-gray-900">
            Price Position
          </span>
        </div>
        <span className="font-normal text-sm text-gray-500 mt-2">
          See how your products are positioned in the market, across different
          prices.
        </span>
      </div>
      {position ? (
        <>
          <div
            className={`flex flex-col container mx-auto px-4 py-8 border-b border-solid border-gray-200 items-center ${
              compact ? 'gap-6' : 'lg:flex-row lg:p-10 gap-8'
            }`}
          >
            <div
              className={
                compact
                  ? 'grid grid-cols-2 gap-x-6 gap-y-4 order-last w-full'
                  : 'grid grid-cols-2 sm:grid-cols-3 gap-x-12 gap-y-8 order-last lg:order-first w-full lg:w-1/2'
              }
            >
              {getPositionSlicePercentages(position).map((slice) => (
                <button
                  key={slice.key}
                  onClick={(): void => {
                    sendEvent(eventNames[slice.key])
                    const isCheap = IS_CHEAP_BY_KEY[slice.key]
                    if (onSliceClick) {
                      onSliceClick(isCheap, slice.key)
                    } else {
                      navigate(
                        `/products?page=1&limit=10&orderBy=name&orderDirection=asc&is_cheap=${isCheap}`,
                      )
                    }
                  }}
                  className={
                    compact
                      ? 'flex flex-col items-center text-center'
                      : 'flex flex-col items-start text-left'
                  }
                >
                  <div className="flex flex-row items-center gap-2">
                    <div
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: slice.color }}
                    />
                    <span
                      className={
                        compact
                          ? 'font-normal text-xs text-gray-500'
                          : 'font-medium text-base text-gray-500'
                      }
                    >
                      {slice.name}
                    </span>
                  </div>
                  <span
                    className={
                      compact
                        ? 'font-normal text-base text-gray-900 pt-1'
                        : 'pl-4 font-normal text-xl text-gray-900 pt-1'
                    }
                  >
                    {position?.[slice.key] ?? 0} products
                  </span>
                </button>
              ))}
            </div>
            <div
              className={
                compact
                  ? 'w-full max-w-[220px] mx-auto order-first'
                  : 'w-full max-w-[300px] mx-auto lg:w-1/2 order-first lg:order-last'
              }
            >
              <PositionDoughnutChart
                position={position}
                showLabels={!compact}
              />
            </div>
          </div>
          <div className="flex justify-end container mx-auto p-6">
            <Link
              to="/products"
              className="font-medium text-sm text-gray-500 p-2"
            >
              View all products
            </Link>
          </div>
        </>
      ) : (
        <div className="h-72 flex justify-center items-center">
          <div className="flex flex-col items-center">
            <SpinLoader />
            <span className="font-medium text-sm text-gray-700 pt-4">
              Calculating...
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
